import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pricingService } from '../../../services/pricingService';
import { PricingPlan } from '../../../types';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const pricingPlans = await pricingService.getAllPlans();
      setPlans(pricingPlans);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanSelect = (planId: string) => {
    if (planId === 'monthly') {
      navigate('/onboarding');
    } else if (planId === 'annual') {
      navigate('/onboarding');
    }
  };

  const handleBackToSignup = () => {
    navigate('/cadastro');
  };

  if (isLoading) {
    return (
      <div className="pricing-page">
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Carregando planos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <button 
            className="bg-transparent border-none text-blue-700 text-base cursor-pointer mb-8 px-4 py-2 rounded-lg transition-colors duration-200 hover:bg-gray-100"
            onClick={handleBackToSignup}
          >
            ← Voltar
          </button>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Escolha o plano que mais combina com você</h1>
          <p className="text-xl text-gray-600 mb-8">
            Invista no seu conhecimento e transforme informação em lucro.
          </p>

          <div className="flex items-center justify-center gap-4 mb-8">
            <span className={!isAnnual ? 'text-blue-700 font-semibold' : 'text-gray-600 font-medium'}>Mensal</span>
            <div className="relative w-16 h-8">
              <input
                type="checkbox"
                id="pricing-toggle"
                className="opacity-0 w-0 h-0"
                checked={isAnnual}
                onChange={(e) => setIsAnnual(e.target.checked)}
              />
              <label htmlFor="pricing-toggle" className={`absolute cursor-pointer top-0 left-0 right-0 bottom-0 bg-gray-200 rounded-full transition-colors duration-300 ${isAnnual ? 'bg-blue-700' : ''}`}>
                <span className={`absolute content-[''] h-6 w-6 left-1 bottom-1 bg-white rounded-full transition-transform duration-300 shadow-sm ${isAnnual ? 'transform translate-x-8' : ''}`}></span>
              </label>
            </div>
            <span className={isAnnual ? 'text-blue-700 font-semibold' : 'text-gray-600 font-medium'}>
              Anual <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full ml-2 font-semibold">-17%</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {plans.map((plan, index) => (
            <div 
              key={plan.id} 
              className={`bg-white rounded-xl shadow-md p-8 border-2 border-gray-200 relative transition-transform duration-300 animate-fadeInUp
                ${plan.isPopular ? 'border-blue-700 scale-105 shadow-lg' : 'hover:-translate-y-1 hover:shadow-lg'}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {plan.isPopular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-br from-blue-700 to-blue-900 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-md">
                  Mais vantajoso
                </div>
              )}
              
              <div className="text-center mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-5xl font-bold text-blue-700">{plan.price}</span>
                  <span className="text-base text-gray-600 font-medium">{plan.period}</span>
                </div>
                {plan.originalPrice && (
                  <div className="text-sm text-gray-600">
                    De <span className="line-through">{plan.originalPrice}</span>
                  </div>
                )}
              </div>

              <ul className="list-none mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-3 mb-4 py-2">
                    <span className="text-xl text-green-500 flex-shrink-0">✅</span>
                    <span className="text-gray-900 leading-snug">{feature}</span>
                  </li>
                ))}
              </ul>

              <button 
                className={`w-full p-4 text-lg font-semibold rounded-lg transition-all duration-200 cursor-pointer
                  ${plan.isPopular ? 'bg-blue-700 text-white hover:bg-blue-800' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                onClick={() => handlePlanSelect(plan.id)}
              >
                {plan.isAnnual ? 'Assinar Anual' : 'Assinar Mensal'}
              </button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="text-3xl flex-shrink-0">🛡️</div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-1">Garantia de 7 dias</h4>
              <p className="text-sm text-gray-600 m-0">Teste sem riscos. Cancele a qualquer momento.</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="text-3xl flex-shrink-0">🔒</div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-1">Pagamento seguro</h4>
              <p className="text-sm text-gray-600 m-0">Seus dados protegidos com criptografia SSL.</p>
            </div>
          </div>
        </div>

        <div className="text-center p-12 bg-gradient-to-br from-blue-900 to-blue-800 text-white rounded-xl shadow-lg">
          <h2 className="text-3xl font-bold mb-4">Pronto para começar sua jornada no mercado de ações?</h2>
          <p className="text-lg opacity-90">Junte-se a milhares de investidores que já transformaram notícias em lucro.</p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;









